import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import FeedbackNotification from '../common/FeedbackNotification';
import LoadingSpinner from '../LoadingSpinner';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText } from '../../security/sanitize';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';

const InviteInfluencerModal = ({ isOpen, onClose, influencer, onInvite }) => {
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Feedback notification state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');

  // Mock campaigns data
  const mockCampaigns = [
    { 
      id: 'camp1', 
      name: 'Summer Fashion Collection',
      status: 'active'
    },
    { 
      id: 'camp2', 
      name: 'Eco-Friendly Skincare',
      status: 'active'
    },
    { 
      id: 'camp3', 
      name: 'Travel Essentials',
      status: 'active'
    }
  ];

  useEffect(() => {
    // In a real implementation, we would fetch campaigns from Firestore
    const fetchCampaigns = async () => {
      try {
        // Simulate API call
        setTimeout(() => {
          // Only show active campaigns
          const activeCampaigns = mockCampaigns.filter(camp => camp.status === 'active');
          setCampaigns(activeCampaigns);
          setLoading(false);
          
          // Reset form when modal opens
          if (isOpen) {
            setSelectedCampaign('');
            setCustomMessage(`Hi ${influencer?.name || 'there'},\n\nWe'd love to collaborate with you on our campaign. Please let us know if you're interested!\n\nRegards,\nThe Brand Team`);
          }
        }, 500);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchCampaigns();
    }
  }, [isOpen, influencer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate CSRF token
    const csrfToken = document.querySelector('input[name="csrfToken"]')?.value;
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      // Show error feedback for CSRF validation failure
      setFeedbackType('error');
      setFeedbackMessage('Security validation failed. Please refresh the page and try again.');
      setShowFeedback(true);
      
      // Clear feedback after animation completes
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);
      
      return;
    }
    
    if (!selectedCampaign) {
      // Show error feedback
      setFeedbackType('error');
      setFeedbackMessage('Please select a campaign');
      setShowFeedback(true);
      
      // Clear feedback after animation completes
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);
      
      return;
    }
    
    // Find the selected campaign to get its name
    const selectedCampaignData = campaigns.find(camp => camp.id === selectedCampaign);
    
    try {
      // Sanitize user input
      const sanitizedMessage = sanitizeText(customMessage);
      
      // Send the invitation with campaign details
      onInvite({
        campaignId: selectedCampaign,
        campaignName: selectedCampaignData?.name || 'Campaign',
        influencerId: influencer.id,
        message: sanitizedMessage,
        status: 'pending',
        timestamp: new Date().toISOString()
      });
      
      // Show success feedback
      setFeedbackType('success');
      setFeedbackMessage('Invitation sent successfully!');
      setShowFeedback(true);
      
      // Close modal after a brief delay
      setTimeout(() => {
        setShowFeedback(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error sending invitation:', error);
      
      // Show error feedback
      setFeedbackType('error');
      setFeedbackMessage('Failed to send invitation. Please try again.');
      setShowFeedback(true);
      
      // Clear feedback after animation completes
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      {/* Feedback Notification */}
      <FeedbackNotification
        show={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={feedbackType}
        message={feedbackMessage}
        autoCloseTime={3000}
      />
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4">
              <h3 className="text-lg font-medium text-gray-900">Invite to Campaign</h3>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <CSRFToken />
                <div className="mb-4">
                  <label htmlFor="campaign" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Campaign
                  </label>
                  <Select
                    value={selectedCampaign}
                    onValueChange={(value) => setSelectedCampaign(value)}
                  >
                    <SelectTrigger id="campaign" className="w-full">
                      <SelectValue placeholder="-- Select a campaign --" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((campaign) => (
                        <SelectItem key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Invitation Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border border-gray-300 rounded-md"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    required
                  ></textarea>
                </div>
                
                <div className="flex justify-end space-x-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    disabled={!selectedCampaign}
                  >
                    Send Invitation
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteInfluencerModal;
